import { Close } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
import { Product } from '@thro-types/leihbar';

import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useShoppingCartItemMutation } from 'api/hooks/leihbar';
import { MultistepForm } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { AvailabilityStep } from './components/AvailabilityStep';
import { OptionsStep } from './components/OptionsStep';
import { cartItemValidationSchema, useAddToCartInitialValues } from './validation';

interface Props {
  handleClose: () => void;
  open: boolean;
  product: Product;
}

const ModalStep = ({ step, product, handleClose }) => {
  switch (step) {
    case 0:
      return <AvailabilityStep product={product} handleClose={handleClose} />;
    case 1:
      return <OptionsStep product={product} />;
    default:
      return null;
  }
};

export const ProductModal = ({ open, handleClose, product }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  const modalSteps = [
    t(translations.pages.leihbar.shoppingCart.steps.availability()),
    t(translations.pages.leihbar.shoppingCart.steps.options())
  ];

  const { create } = useShoppingCartItemMutation();

  const handleSubmit = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const data = { ...values, product: product };
    try {
      await create.mutateAsync({
        // TODO reduce this to data: data
        amount: data.amount ? data.amount : 1,
        startTime: data.startTime,
        endTime: data.endTime,
        comment: data.comment,
        productId: product.id,
        optionIds: data.options?.map(option => option.id)
      });
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.shoppingCart.alerts.successfulCreate()))
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.leihbar.shoppingCart.alerts.failedCreate()))
      );
    }
    handleClose();
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={'lg'} fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t(translations.pages.leihbar.shoppingCart.modalTitle())}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme => theme.palette.grey[500]
        }}
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        <Formik
          initialValues={useAddToCartInitialValues()}
          validationSchema={yup.object().shape(cartItemValidationSchema(t, product))}
          onSubmit={handleSubmit}
        >
          <MultistepForm
            onCancel={handleClose}
            steps={modalSteps}
            submitting={submitting}
            isStepperClickable={true}
            customSaveText={t(translations.pages.leihbar.addToCart())}
          >
            {step => (
              <Grid container>
                <ModalStep step={step} product={product} handleClose={handleClose} />
              </Grid>
            )}
          </MultistepForm>
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
